import React, { useEffect, useState } from "react";
import { required, isProbablyEmail, includedIn } from "redux-freeform";
import {
  Box,
  ButtonWithAction,
  Cluster,
  FormContainer,
  FormInput,
  FormInputColumn,
  FormSelect,
  Heading,
  SearchableSelect,
  Stack,
  Text,
} from "@thecb/components";
import {
  ACTIVATE_ADMINISTERED_USER,
  DEACTIVATE_ADMINISTERED_USER,
  EDIT_ADMINISTERED_USER,
  PENDING,
  SUCCESSFUL,
  userWithStatusCanBeActivated,
} from "../AdministeredUsers.state";

import {
  AGENCY_ADMIN,
  ALL_POS_ROLE_OPTIONS,
  ALL_REVM_ROLE_OPTIONS,
  CLIENT_ADMIN,
  NO_ROLE,
  OKTA_AGENCY_ADMIN,
  OKTA_CLIENT_ADMIN,
  roleDictionary,
} from "@/sanctuary/typedefs";
import {
  ACTIVE_PRIMARY,
  BACKGROUND_GREY,
  CITYVILLE_GREY,
  ERROR_AMOUNT,
} from "@/constants/colors";
import {
  ADD,
  CREATE_CLIENT_ADMIN,
  EDIT,
  PERSONAL_INFORMATION,
  PROFILE,
} from "@/constants/formTypes";
import { isCBAdminRole, isClientAdminRole, noop } from "@/util/common";

export const InternalUserInfoForm = ({
  actions,
  activateUser,
  allAgencyOptions,
  allSelected,
  clearOnDismount,
  clientOptions,
  closeForm,
  currentUserState,
  currentUserRole,
  deactivateUser,
  disableRole,
  emailDisabled,
  fields,
  firstName,
  formType,
  handleSubmit = noop,
  isActive,
  isPOSEnabled,
  lastName,
  matchedUserAgencies,
  modificationStatuses,
  namesDisabled,
  openChangePasswordForm,
  roleCombinations: posRoleCombinations,
  roleOptions,
  selectAgency,
  selectedAgencies,
  selectionDisabled,
  setDisableSelection,
  showErrors,
  toggleSelectAllAgencies = noop,
  userBeingEdited,
  userBeingEditedRole,
  userCanActivateUser,
  userCanDeactivateUser,
  userId,
  userIsBeingChecked,
  userStatus,
  variant = "default",
}) => {
  const canEditOwnPassword =
    currentUserState?.credentialsProvider?.value !== "FEDERATION";

  // If no role exists yet, default it to NO_ROLE for revm and cb-pos
  const [revmRole, setRevmRole] = useState(userBeingEditedRole || NO_ROLE);
  const [posRole, setPosRole] = useState(
    userBeingEdited?.value?.pos_role?.value?.value || NO_ROLE
  );

  const userModificationStatus = modificationStatuses?.[userId];
  const safeStatus = userModificationStatus?.status ?? SUCCESSFUL;
  const safeModificationType = userModificationStatus?.modificationType;
  const userIsUpdating = safeStatus === PENDING;
  const userIsDeactivating =
    userIsUpdating && safeModificationType === DEACTIVATE_ADMINISTERED_USER;
  const userIsActivating =
    userIsUpdating && safeModificationType === ACTIVATE_ADMINISTERED_USER;
  const userIsBeingEdited =
    userIsUpdating && safeModificationType === EDIT_ADMINISTERED_USER;
  const userCanBeActivated = userWithStatusCanBeActivated(userStatus);

  const handleUserPermissions = () => {
    const isClientAdmin = isClientAdminRole(currentUserRole);
    const isCBAdmin = isCBAdminRole(currentUserRole);
    const isAgencyAdmin =
      currentUserRole === AGENCY_ADMIN || currentUserRole === OKTA_AGENCY_ADMIN;
    const isNotEditingClientAdmin = !(
      userBeingEditedRole === CLIENT_ADMIN ||
      userBeingEditedRole === OKTA_CLIENT_ADMIN
    );
    const hasNoMatchingAgencies = matchedUserAgencies.length === 0;

    return (
      isCBAdmin ||
      isClientAdmin ||
      (isAgencyAdmin && isNotEditingClientAdmin) ||
      hasNoMatchingAgencies
    );
  };

  const clientErrorMessages = {
    [required.error]: "Client is required",
  };
  const firstNameErrorMessages = {
    [required.error]: "First name is required",
  };
  const lastNameErrorMessages = {
    [required.error]: "Last name is required",
  };
  const emailErrorMessages = {
    [required.error]: "Email address is required",
    [isProbablyEmail.error]: "Invalid email address",
  };
  const roleErrorMessages = {
    [required.error]: "Revenue Management role is required",
  };

  const posRoleErrorMessages = {
    [includedIn.error]: "This role combination is not allowed",
  };
  // eslint-disable-next-line
  const [revmRoleOptions, _setRevmRoleOptions] = useState(
    formType == CREATE_CLIENT_ADMIN
      ? [NO_ROLE, CLIENT_ADMIN]
      : ALL_REVM_ROLE_OPTIONS.filter?.((role) => roleOptions?.includes(role))
  );

  const [posRoleOptions, setPosRoleOptions] = useState(
    formType == CREATE_CLIENT_ADMIN
      ? [NO_ROLE, CLIENT_ADMIN]
      : ALL_POS_ROLE_OPTIONS.filter?.(
          (role) =>
            posRoleCombinations?.[revmRole]?.includes(role) ||
            (role === revmRole && !posRoleCombinations?.[revmRole]?.includes(role))
        )
  );
  const [disabledPosRoleOptions, setDisabledPosRoleOptions] = useState(
    ALL_POS_ROLE_OPTIONS.filter((role) => !posRoleOptions?.includes(role))
  );

  const isPosRoleDisabled =
    (formType === EDIT && !isActive) ||
    isCBAdminRole(revmRole) ||
    isCBAdminRole(posRole);
  const isValidConfigForPOSRoleAssignment =
    isPOSEnabled && posRoleOptions?.length > 0;
  const disabledRoleStyles = `{
    background-color: ${BACKGROUND_GREY};
    cursor: default;
    pointer-events: none;
    margin-top: 0;
    margin-bottom: 0;

    > ul {margin-bottom: 0;}
    span {
      color: ${CITYVILLE_GREY};
    }
  }`;

  useEffect(() => {
    if (formType === CREATE_CLIENT_ADMIN) {
      actions.fields.client.addValidator(required());
    }
    if (formType !== PROFILE) {
      actions.fields.email.addValidator(required());
    }
    if (formType !== PROFILE && formType !== PERSONAL_INFORMATION) {
      actions.fields.role.addValidator(required());
    }
    if (isValidConfigForPOSRoleAssignment) {
      actions.fields.role.set(revmRole);
      actions.fields.pos_role.set(posRole);
      actions.fields.pos_role.addValidator(
        includedIn(posRoleCombinations[revmRole])
      );
    } else {
      actions.fields.role.set(revmRole);
    }
    // Below comment necessary to avoid unnecessary dependency additions
    // eslint-disable-next-line
  }, [
    actions.fields.client,
    actions.fields.email,
    actions.fields.role,
    actions.fields.pos_role,
    formType,
    isValidConfigForPOSRoleAssignment,
    posRoleCombinations,
    revmRole,
  ]);

  useEffect(
    () => () => {
      if (clearOnDismount) {
        actions.form.clear();
      }
    },
    [actions.form, clearOnDismount]
  );

  return (
    <FormContainer
      variant={variant}
      role="form"
      aria-label="user-info-form"
      extraStyles={`padding: 0;`}
    >
      {formType === PROFILE && (
        <Box padding="1.5rem">
          <Cluster justify="flex-start" align="center">
            <Box
              extraStyles={{
                padding: "1.25rem",
                borderRadius: "50%",
                backgroundColor: BACKGROUND_GREY,
              }}
            >
              <Heading variant="h4" weight="700">
                {firstName.charAt(0).toUpperCase()}
                {lastName.charAt(0).toUpperCase()}
              </Heading>
            </Box>
            <Text variant="p" weight="700" extraStyles={`padding-left: 1rem;`}>
              {firstName} {lastName}
            </Text>
          </Cluster>
        </Box>
      )}
      {formType === CREATE_CLIENT_ADMIN && (
        <Box
          padding="1.5rem 1.5rem 1rem"
          borderColor={BACKGROUND_GREY}
          borderSize="1px"
          borderWidthOverride={formType === PROFILE ? "1px 0 0" : "0 0 0 0"}
        >
          <FormInputColumn>
            <Heading variant="h6" weight="700" margin="0 0 0.5rem">
              Select Client
            </Heading>
            <FormSelect
              labelTextWhenNoError="Client"
              errorMessages={clientErrorMessages}
              options={clientOptions.map((client) => ({
                text: client,
                value: client,
                id: client,
              }))}
              field={fields.client}
              fieldActions={actions.fields.client}
              showErrors={showErrors}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
            />
          </FormInputColumn>
        </Box>
      )}
      <Box
        padding={
          formType === PROFILE || formType === PERSONAL_INFORMATION
            ? "1.5rem 1.5rem 0"
            : "1.5rem 1.5rem 0.5rem"
        }
        borderSize="1px"
        borderColor={BACKGROUND_GREY}
        borderWidthOverride={
          formType === CREATE_CLIENT_ADMIN
            ? "1px 0 1px"
            : formType === PROFILE
            ? "1px 0 0 0"
            : formType === PERSONAL_INFORMATION
            ? "0"
            : "0 0 1px"
        }
      >
        <FormInputColumn>
          <Heading variant="h6" weight="700" margin="0 0 0.5rem">
            Personal Information
          </Heading>
          <FormInput
            labelTextWhenNoError="First name"
            errorMessages={firstNameErrorMessages}
            field={fields.firstName}
            fieldActions={actions.fields.firstName}
            showErrors={showErrors}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
            disabled={namesDisabled}
            autocompleteValue="given-name"
          />
          <FormInput
            labelTextWhenNoError="Last name"
            errorMessages={lastNameErrorMessages}
            field={fields.lastName}
            fieldActions={actions.fields.lastName}
            showErrors={showErrors}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
            disabled={namesDisabled}
            autocompleteValue="family-name"
          />
          <FormInput
            labelTextWhenNoError="Email"
            errorMessages={emailErrorMessages}
            field={fields.email}
            fieldActions={actions.fields.email}
            showErrors={showErrors}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
            disabled={emailDisabled}
            autocompleteValue="email"
            extraStyles={{ marginBottom: 0 }}
            decorator={
              canEditOwnPassword &&
              formType === PROFILE && (
                <Text
                  variant="pS"
                  color={`#15749D`}
                  onClick={openChangePasswordForm}
                  extraStyles={`display: block; cursor: pointer; padding: 0.25rem 0 1.5rem`}
                >
                  Reset Password
                </Text>
              )
            }
          />
        </FormInputColumn>
      </Box>
      {(formType === ADD || formType === EDIT) && (
        <Box padding="0.5rem 1.5rem 1.5rem">
          <FormInputColumn>
            <Heading variant="h6" weight="700" margin="1rem 0 -0.5rem">
              Agencies
            </Heading>
            <Text as="span" extraStyles={`padding-bottom: 0.5rem;`}>
              Set the agencies the user can see
            </Text>
            <Text as="label" variant="pS">
              Agencies
            </Text>
            <SearchableSelect
              actions={actions}
              fields={fields}
              items={allAgencyOptions}
              selectedItems={selectedAgencies}
              allSelected={
                allSelected ||
                (formType === EDIT &&
                  (isClientAdminRole(revmRole) ||
                    (isPOSEnabled && isClientAdminRole(posRole))))
              }
              toggleSelectAllItems={toggleSelectAllAgencies}
              selectItem={selectAgency}
              disabled={
                selectionDisabled ||
                (formType === EDIT && !isActive) ||
                isCBAdminRole(revmRole) ||
                isCBAdminRole(posRole) ||
                isClientAdminRole(revmRole) ||
                isClientAdminRole(posRole)
              }
            />
          </FormInputColumn>
        </Box>
      )}
      {formType !== PROFILE && formType !== PERSONAL_INFORMATION && (
        <Box
          padding={
            formType === CREATE_CLIENT_ADMIN
              ? "0.5rem 1.5rem 0rem"
              : "0.5rem 1.5rem 0.5rem"
          }
          borderColor={BACKGROUND_GREY}
          borderSize="1px"
          borderWidthOverride={formType === CREATE_CLIENT_ADMIN ? "0" : "1px 0 0"}
        >
          <Box padding="0" margin="0">
            <FormInputColumn>
              <Heading variant="h6" weight="700" margin="1rem 0 -0.5rem">
                Role
              </Heading>
              <Text as="span" extraStyles={{ paddingBottom: "0.5rem" }}>
                To give a user access to an application you must set a role for them.
                If they have no role they will not be able to access that
                application.
              </Text>

              <FormSelect
                labelTextWhenNoError="Revenue Management role"
                errorMessages={roleErrorMessages}
                options={revmRoleOptions.map((role) => ({
                  text: roleDictionary[role],
                  value: role,
                  id: role,
                }))}
                field={fields.role}
                fieldActions={actions.fields.role}
                showErrors={showErrors}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
                disabled={
                  disableRole || isCBAdminRole(revmRole) || isCBAdminRole(posRole)
                }
                extraStyles={disableRole ? disabledRoleStyles : {}}
                onChange={(newValue) => {
                  setRevmRole(newValue);
                  actions.fields.role.set(newValue);
                  if (
                    isClientAdminRole(newValue) ||
                    isClientAdminRole(posRole) ||
                    isCBAdminRole(newValue) ||
                    isCBAdminRole(posRole)
                  ) {
                    toggleSelectAllAgencies(true);
                    setDisableSelection(true);
                  } else {
                    setDisableSelection(false);
                  }

                  if (isPOSEnabled && posRoleCombinations) {
                    setPosRoleOptions(posRoleCombinations[newValue]);
                    setDisabledPosRoleOptions(
                      ALL_POS_ROLE_OPTIONS.filter(
                        (role) => !posRoleCombinations[newValue]?.includes(role)
                      )
                    );
                    // Reset the Cashiering Role menu to NO_ROLE to make business requirements easier to enforce
                    actions.fields.pos_role.clear();
                    actions.fields.pos_role.set(NO_ROLE);
                    setPosRole(NO_ROLE);
                  }
                }}
              />

              {/* There should not be any scenarios where clients NOT configured
                for Okta are working with Cashiering roles in RevM */}
              {isPOSEnabled && posRoleOptions && (
                <FormSelect
                  labelTextWhenNoError="Cashiering role"
                  errorMessages={posRoleErrorMessages}
                  options={[...posRoleOptions, ...disabledPosRoleOptions].map?.(
                    (role) => ({
                      text: roleDictionary[role],
                      value: role,
                      id: role,
                    })
                  )}
                  onChange={(newValue) => {
                    if (
                      isClientAdminRole(newValue) ||
                      isClientAdminRole(revmRole) ||
                      isCBAdminRole(newValue) ||
                      isCBAdminRole(revmRole)
                    ) {
                      toggleSelectAllAgencies(true);
                      setDisableSelection(true);
                    } else {
                      setDisableSelection(false);
                    }
                    actions.fields.pos_role.set(newValue);
                    setPosRole(newValue);
                  }}
                  disabledValues={disabledPosRoleOptions}
                  field={fields.pos_role}
                  fieldActions={actions.fields.pos_role}
                  showErrors={showErrors}
                  onKeyDown={(e) => {
                    e.preventDefault();
                    e.key === "Enter" && handleSubmit(e);
                  }}
                  disabled={isPosRoleDisabled}
                  extraStyles={isPosRoleDisabled ? disabledRoleStyles : {}}
                />
              )}
            </FormInputColumn>
          </Box>
        </Box>
      )}
      <Box
        padding={
          formType === CREATE_CLIENT_ADMIN
            ? "0 1.5rem 1.5rem"
            : formType === PERSONAL_INFORMATION
            ? "0.25rem 1.5rem 1.5rem"
            : "0.5rem 1.5rem 1.5rem"
        }
        borderSize="0"
        borderWidthOverride="0 0 0 0"
      >
        <Stack childGap={".5rem"} direction="row" justify="flex-end">
          {formType === EDIT &&
            isActive &&
            userCanDeactivateUser &&
            handleUserPermissions() && (
              <ButtonWithAction
                className="primary-button"
                text="Deactivate User"
                action={userIsUpdating ? noop : () => deactivateUser(userId)}
                isLoading={userIsDeactivating}
                loadingColor={ERROR_AMOUNT}
                variant={"dangerSecondary"}
                borderRadius="4px"
                extraStyles={`margin: 0 auto 0 0; min-width: 157px;`}
              />
            )}
          {formType === EDIT &&
            !isActive &&
            userCanBeActivated &&
            userCanActivateUser &&
            handleUserPermissions() && (
              <ButtonWithAction
                className="primary-button"
                text="Reactivate User"
                action={userIsUpdating ? noop : () => activateUser(userId)}
                isLoading={userIsActivating}
                loadingColor={ACTIVE_PRIMARY}
                variant={"secondary"}
                borderRadius="4px"
                extraStyles={`margin: 0 auto 0 0; min-width: 157px;`}
              />
            )}
          <ButtonWithAction
            text="Cancel"
            action={() => {
              if (formType !== CREATE_CLIENT_ADMIN) {
                toggleSelectAllAgencies(false);
              }
              closeForm();
            }}
            variant={"secondary"}
            dataQa="Cancel"
            extraStyles={`margin: 0rem; min-width: 98px; border-radius: 4px;`}
          />
          <ButtonWithAction
            className="primary-button"
            text={
              formType === PERSONAL_INFORMATION
                ? "Continue"
                : formType === ADD
                ? "Add User"
                : "Save Changes"
            }
            action={(e) =>
              userIsUpdating || (formType === EDIT && !isActive)
                ? noop
                : handleSubmit(e)
            }
            isLoading={
              formType === PERSONAL_INFORMATION
                ? userIsBeingChecked
                : userIsBeingEdited
            }
            variant={"primary"}
            disabled={formType === EDIT && !isActive}
            dataQa="Save"
            extraStyles={`margin: 0rem; border-radius: 4px;`}
            data-testid="internal-user-submit-button"
          />
        </Stack>
      </Box>
    </FormContainer>
  );
};
